/**
 * Builds the canvas filter used to brighten low-light camera video.
 *
 * A plain {@code brightness()} multiply scales every pixel linearly, which pushes bright
 * areas (foreheads, glasses, shiny skin) past white and exaggerates reflections. Google
 * Meet's "adjust brightness" instead lifts shadows and midtones while leaving highlights
 * essentially untouched — the image looks "whiter" with fewer blown reflections.
 *
 * That is exactly a gamma curve (output = input ^ exponent, exponent < 1). We apply it via a
 * tiny inline SVG {@code feComponentTransfer} filter referenced from the canvas context. When
 * a browser does not support SVG filters on a canvas, we fall back to a CSS approximation.
 */
/**
 * The id of the shared inline SVG gamma filter.
 */
const FILTER_ID = 'jitsi-brightness-gamma';
/**
 * Lower bound for the gamma exponent. Smaller exponents lift more aggressively; clamped so a
 * very dark scene does not turn milky/flat.
 */
const MIN_EXPONENT = 0.42;
/**
 * Upper bound for the gamma exponent (closest to a no-op lift).
 */
const MAX_EXPONENT = 0.95;
/**
 * Extra saturation added per unit of boost. Gamma slightly desaturates as it lifts, so we
 * re-saturate a touch to keep skin tones natural.
 */
const SATURATE_FACTOR = 0.2;
let feFuncR = null;
let feFuncG = null;
let feFuncB = null;
let svgSupported = null;
/**
 * Lazily creates the shared inline SVG gamma filter and appends it to the document.
 *
 * @returns {void}
 */
function ensureSvgFilter() {
    if (feFuncR || typeof document === 'undefined' || !document.body) {
        return;
    }
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('width', '0');
    svg.setAttribute('height', '0');
    svg.setAttribute('aria-hidden', 'true');
    svg.style.position = 'absolute';
    svg.style.width = '0';
    svg.style.height = '0';
    svg.style.pointerEvents = 'none';
    const filter = document.createElementNS(ns, 'filter');
    filter.setAttribute('id', FILTER_ID);
    // Apply the gamma on the stored (sRGB) pixel values so the lift matches what is on screen.
    filter.setAttribute('color-interpolation-filters', 'sRGB');
    const transfer = document.createElementNS(ns, 'feComponentTransfer');
    feFuncR = document.createElementNS(ns, 'feFuncR');
    feFuncG = document.createElementNS(ns, 'feFuncG');
    feFuncB = document.createElementNS(ns, 'feFuncB');
    [feFuncR, feFuncG, feFuncB].forEach(fn => {
        fn.setAttribute('type', 'gamma');
        fn.setAttribute('amplitude', '1');
        fn.setAttribute('exponent', '0.6');
        fn.setAttribute('offset', '0');
        transfer.appendChild(fn);
    });
    filter.appendChild(transfer);
    svg.appendChild(filter);
    document.body.appendChild(svg);
}
/**
 * Updates the gamma exponent on the shared SVG filter.
 *
 * @param {number} exponent - The gamma exponent to apply on each RGB channel.
 * @returns {void}
 */
function setExponent(exponent) {
    const value = String(exponent);
    feFuncR?.setAttribute('exponent', value);
    feFuncG?.setAttribute('exponent', value);
    feFuncB?.setAttribute('exponent', value);
}
/**
 * Feature-detects whether SVG filters can be applied to a canvas 2D context in this browser.
 * Runs once: draws a mid-gray pixel through a strong gamma and checks it brightened.
 *
 * @returns {boolean} True when canvas SVG filters take effect.
 */
function detectSvgFilterSupport() {
    ensureSvgFilter();
    if (!feFuncR) {
        return false;
    }
    try {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
            return false;
        }
        setExponent(0.5);
        ctx.filter = `url(#${FILTER_ID})`;
        ctx.fillStyle = 'rgb(64, 64, 64)';
        ctx.fillRect(0, 0, 1, 1);
        // 64/255 = 0.251; 0.251^0.5 ≈ 0.501 → ~128 when the filter is applied, ~64 when not.
        const red = ctx.getImageData(0, 0, 1, 1).data[0];
        return red > 90;
    }
    catch {
        return false;
    }
}
/**
 * Returns the canvas {@code ctx.filter} string that brightens the frame for the given boost.
 * Uses a gamma curve (whiter, fewer reflections — Google Meet style) when supported, and a
 * CSS brightness/contrast/saturate approximation otherwise.
 *
 * @param {number} brightness - The brightness multiplier. Values at or below 1 disable the filter.
 * @returns {string} The filter string, or 'none' when no adjustment is needed.
 */
export function getBrightnessFilter(brightness) {
    if (brightness <= 1) {
        return 'none';
    }
    if (svgSupported === null) {
        svgSupported = detectSvgFilterSupport();
    }
    if (svgSupported) {
        const exponent = Math.max(MIN_EXPONENT, Math.min(MAX_EXPONENT, 1 / brightness));
        const saturate = 1 + ((brightness - 1) * SATURATE_FACTOR);
        setExponent(exponent);
        return `url(#${FILTER_ID}) saturate(${saturate.toFixed(3)})`;
    }
    // Fallback for browsers without canvas SVG filter support.
    const boost = brightness - 1;
    const contrast = Math.max(0.5, 1 - (boost * 0.15));
    const saturate = 1 + (boost * 0.35);
    return `brightness(${brightness}) contrast(${contrast.toFixed(3)}) saturate(${saturate.toFixed(3)})`;
}
